import {
  useContactTicketMonthQuery,
  getContactIdsWithTicketsInRange,
  paginateContactNodes,
  type TicketFilterParams,
} from "./contacts-ticket-filter-utils";

function getThisMonthRange() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
  };
}

function getLastMonthRange() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
    end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
  };
}

function processContactTicketFilter(
  result: ReturnType<typeof useContactTicketMonthQuery>,
  start: Date,
  end: Date,
  mode: "with" | "without",
  params?: TicketFilterParams
) {
  const processedData = result.data
    ? (() => {
        const allContacts =
          result.data.contactsCollection?.edges
            ?.map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => node != null) || [];

        const contactIdsWithTickets = getContactIdsWithTicketsInRange(result.data, start, end);

        const filtered =
          mode === "without"
            ? allContacts.filter((c) => !contactIdsWithTickets.has(c.id))
            : allContacts.filter((c) => contactIdsWithTickets.has(c.id));

        return paginateContactNodes(filtered, params);
      })()
    : undefined;

  return {
    ...result,
    data: processedData,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

/** Get contacts that have NO tickets created this month. */
export function useContactsWithNoTicketsThisMonth(params?: TicketFilterParams) {
  const result = useContactTicketMonthQuery(params);
  const { start, end } = getThisMonthRange();
  return processContactTicketFilter(result, start, end, "without", params);
}

/** Get contacts that HAVE tickets created this month. */
export function useContactsWithTicketsThisMonth(params?: TicketFilterParams) {
  const result = useContactTicketMonthQuery(params);
  const { start, end } = getThisMonthRange();
  return processContactTicketFilter(result, start, end, "with", params);
}

/** Get contacts that have NO tickets created last month. */
export function useContactsWithNoTicketsLastMonth(params?: TicketFilterParams) {
  const result = useContactTicketMonthQuery(params);
  const { start, end } = getLastMonthRange();
  return processContactTicketFilter(result, start, end, "without", params);
}

/** Get contacts that HAVE tickets created last month. */
export function useContactsWithTicketsLastMonth(params?: TicketFilterParams) {
  const result = useContactTicketMonthQuery(params);
  const { start, end } = getLastMonthRange();
  return processContactTicketFilter(result, start, end, "with", params);
}
